import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, Save, RotateCcw, ShieldCheck, Loader2, Truck, DollarSign, CalendarOff,
} from 'lucide-react';
import { toast as sonner } from './ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  correction: { label: 'Payroll Corrections', icon: DollarSign },
  leave:      { label: 'Time-off',             icon: CalendarOff },
  advance:    { label: 'Cash Advances',        icon: Truck },
};

const VALID_ROLES = [
  { key: 'dispatcher',    label: 'Dispatcher' },
  { key: 'company_admin', label: 'Company Admin' },
  { key: 'super_admin',   label: 'Super Admin' },
];

const MAX_STEPS = 6;

/**
 * ApprovalChainsConfig
 * ────────────────────
 * Admin tool to view + edit the multi-step approval chain for each request
 * kind (correction | leave | advance).  Defaults are surfaced read-only;
 * once edited, a saved chain replaces the default.
 */
const ApprovalChainsConfig = () => {
  const [loading, setLoading] = useState(true);
  const [chains, setChains] = useState([]);   // [{kind, steps, is_default, ...}]
  const [savingKind, setSavingKind] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/approval-chains`, { withCredentials: true });
        setChains(res.data);
      } catch (err) {
        sonner.error(err.response?.data?.detail || 'Failed to load chains');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateStep = (kind, i, patch) => {
    setChains((prev) => prev.map((c) => {
      if (c.kind !== kind) return c;
      const steps = c.steps.map((s, idx) => idx === i ? { ...s, ...patch } : s);
      return { ...c, steps, dirty: true };
    }));
  };

  const addStep = (kind) => {
    setChains((prev) => prev.map((c) => {
      if (c.kind !== kind) return c;
      if (c.steps.length >= MAX_STEPS) return c;
      return {
        ...c,
        steps: [...c.steps, { label: '', approver_role: 'company_admin' }],
        dirty: true,
      };
    }));
  };

  const removeStep = (kind, i) => {
    setChains((prev) => prev.map((c) => {
      if (c.kind !== kind) return c;
      if (c.steps.length <= 1) return c;
      return { ...c, steps: c.steps.filter((_, idx) => idx !== i), dirty: true };
    }));
  };

  const handleSave = async (kind) => {
    const ch = chains.find((c) => c.kind === kind);
    if (!ch) return;
    if (ch.steps.some((s) => !s.label.trim())) {
      sonner.error('Every step needs a label');
      return;
    }
    setSavingKind(kind);
    try {
      const res = await axios.put(
        `${API}/approval-chains/${kind}`,
        { steps: ch.steps.map((s) => ({ label: s.label.trim(), approver_role: s.approver_role })) },
        { withCredentials: true },
      );
      setChains((prev) => prev.map((c) => c.kind === kind ? { ...res.data, dirty: false } : c));
      sonner.success(`${KIND_META[kind].label} chain saved`);
    } catch (err) {
      sonner.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingKind(null);
    }
  };

  const handleReset = async (kind) => {
    if (!window.confirm(`Reset ${KIND_META[kind].label} to the default single-step chain?`)) return;
    setSavingKind(kind);
    try {
      await axios.delete(`${API}/approval-chains/${kind}`, { withCredentials: true });
      // Refresh that kind from server (it'll now be the default)
      const res = await axios.get(`${API}/approval-chains`, { withCredentials: true });
      setChains(res.data);
      sonner.success(`Reset ${KIND_META[kind].label} to default`);
    } catch (err) {
      sonner.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setSavingKind(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-6 flex items-center justify-center text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading chains…
      </div>
    );
  }

  return (
    <div data-testid="approval-chains-config" className="space-y-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div>
        <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-1 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          <ShieldCheck className="w-5 h-5 text-[#002FA7]" />
          Approval Chains
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Configure who must approve each request type — up to {MAX_STEPS} steps. The same person cannot approve two steps in a single chain (anti-collusion).
        </p>
      </div>

      {chains.map((c) => {
        const meta = KIND_META[c.kind] || { label: c.kind, icon: ShieldCheck };
        const Icon = meta.icon;
        return (
          <div
            key={c.kind}
            data-testid={`chain-config-${c.kind}`}
            className={`border rounded-md p-4 transition-colors ${c.dirty ? 'border-[#002FA7] bg-blue-50/30' : 'border-slate-200 bg-white'}`}
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-[#002FA7]" />
                <span className="font-semibold text-slate-900">{meta.label}</span>
                {c.is_default && !c.dirty && (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500 bg-slate-100 rounded">default</span>
                )}
                {c.dirty && (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#002FA7] bg-blue-100 rounded">unsaved</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!c.is_default && (
                  <button
                    data-testid={`chain-reset-${c.kind}`}
                    onClick={() => handleReset(c.kind)}
                    disabled={savingKind === c.kind}
                    className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-2 py-1 rounded transition-colors disabled:opacity-50"
                  >
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                )}
                <button
                  data-testid={`chain-save-${c.kind}`}
                  onClick={() => handleSave(c.kind)}
                  disabled={!c.dirty || savingKind === c.kind}
                  className="inline-flex items-center gap-1.5 bg-[#002FA7] text-white px-3 py-1.5 text-xs rounded-md font-semibold hover:bg-[#00247D] transition-colors disabled:opacity-50"
                >
                  {savingKind === c.kind ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Save
                </button>
              </div>
            </div>

            <ol className="space-y-2">
              {c.steps.map((s, i) => (
                <li key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded p-2" data-testid={`chain-step-${c.kind}-${i}`}>
                  <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                  <input
                    data-testid={`chain-step-${c.kind}-${i}-label`}
                    value={s.label}
                    onChange={(e) => updateStep(c.kind, i, { label: e.target.value })}
                    placeholder="Step label, e.g. Dispatcher review"
                    className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:border-[#002FA7] bg-white"
                  />
                  <select
                    data-testid={`chain-step-${c.kind}-${i}-role`}
                    value={s.approver_role}
                    onChange={(e) => updateStep(c.kind, i, { approver_role: e.target.value })}
                    className="px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:border-[#002FA7] bg-white"
                  >
                    {VALID_ROLES.map((r) => (
                      <option key={r.key} value={r.key}>{r.label}</option>
                    ))}
                  </select>
                  <button
                    data-testid={`chain-step-${c.kind}-${i}-remove`}
                    onClick={() => removeStep(c.kind, i)}
                    disabled={c.steps.length <= 1}
                    className="p-1 text-slate-400 hover:text-red-600 disabled:opacity-30"
                    title={c.steps.length <= 1 ? 'A chain needs at least one step' : 'Remove this step'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ol>

            <button
              data-testid={`chain-add-${c.kind}`}
              onClick={() => addStep(c.kind)}
              disabled={c.steps.length >= MAX_STEPS}
              className="mt-2 inline-flex items-center gap-1 text-xs text-[#002FA7] hover:bg-blue-50 px-2 py-1 rounded transition-colors disabled:opacity-50"
            >
              <Plus className="w-3 h-3" /> Add step{c.steps.length >= MAX_STEPS ? ` (max ${MAX_STEPS})` : ''}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default ApprovalChainsConfig;
